import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Portfolio from './pages/Portfolio';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import ServiceDetail from './pages/services/ServiceDetail';
import AdminPanel from './pages/admin/AdminPanel';
import Login from './pages/admin/Login';
import Privacy from './pages/Privacy';
import PerformanceReport from './components/PerformanceReport';
import ThemeSwitcher from './components/ThemeSwitcher';
import LanguageContext from './contexts/LanguageContext';
import ThemeContext from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/admin/ProtectedRoute';
import { getSeasonalTheme, getThemeStyles } from './services/themeService';
import { initFireworks, destroyFireworks } from './services/fireworks';
import './App.scss';

function App() {
  const [language, setLanguage] = useState('en');
  const [theme, setTheme] = useState(() => getSeasonalTheme());

  // Load language from localStorage or default to 'en'
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setLanguage(savedLanguage);
  }, []);

  // Save language to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Apply theme styles
  useEffect(() => {
    // Remove any existing theme styles to avoid duplicates
    const existing = document.getElementById('theme-styles');
    if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
    }

    const styleElement = document.createElement('style');
    styleElement.id = 'theme-styles';
    styleElement.innerHTML = getThemeStyles(theme);
    document.head.appendChild(styleElement);

    return () => {
      if (styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, [theme]);

  // Create snowflakes for Christmas theme, firework debris for CNY theme, and bubbles for default theme
  useEffect(() => {
    // Remove all existing animation containers
    const containerSelectors = ['.snowflakes', '.bubbles', '.fireworks', '.fireworks-container'];
    containerSelectors.forEach(sel => {
      const el = document.querySelector(sel);
      if (el && el.parentNode) el.parentNode.removeChild(el);
    });

    // Remove all existing animation styles
    const styleIds = ['snowflake-style', 'fireworks-style', 'bubbles-style'];
    styleIds.forEach(id => {
      const el = document.getElementById(id);
      if (el && el.parentNode) el.parentNode.removeChild(el);
    });
    
    if (theme === 'christmas') {
      // Create snowflake container
      const snowflakesContainer = document.createElement('div');
      snowflakesContainer.className = 'snowflakes';
      snowflakesContainer.style.position = 'fixed';
      snowflakesContainer.style.top = '0';
      snowflakesContainer.style.left = '0';
      snowflakesContainer.style.width = '100%';
      snowflakesContainer.style.height = '100%';
      snowflakesContainer.style.pointerEvents = 'none';
      snowflakesContainer.style.zIndex = '9999';
      snowflakesContainer.style.overflow = 'hidden';
      document.body.appendChild(snowflakesContainer);

      // Create snowflakes
      const snowflakeCount = 50;
      
      for (let i = 0; i < snowflakeCount; i++) {
        const snowflake = document.createElement('div');
        
        // Random size between 2px and 6px
        const size = Math.random() * 4 + 2;
        snowflake.style.width = `${size}px`;
        snowflake.style.height = `${size}px`;
        
        // Random position
        snowflake.style.position = 'absolute';
        snowflake.style.left = `${Math.random() * 100}%`;
        snowflake.style.top = `${Math.random() * -100}px`; // Start above viewport
        
        // Styling
        snowflake.style.background = 'white';
        snowflake.style.borderRadius = '50%';
        snowflake.style.opacity = Math.random() * 0.5 + 0.3;
        snowflake.style.pointerEvents = 'none';
        
        // Random fall animation (10-20 seconds)
        const fallDuration = Math.random() * 10 + 10;
        snowflake.style.animation = `fall ${fallDuration}s linear infinite`;
        
        // Random animation delay
        const delay = Math.random() * 5;
        snowflake.style.animationDelay = `${delay}s`;
        
        snowflakesContainer.appendChild(snowflake);
      }

      // Cleanup function
      return () => {
        if (document.body.contains(snowflakesContainer)) {
          document.body.removeChild(snowflakesContainer);
        }
      };
    } else if (theme === 'cny') {
      // Create fireworks container
      const fireworksContainer = document.createElement('div');
      fireworksContainer.className = 'fireworks';
      fireworksContainer.style.position = 'fixed';
      fireworksContainer.style.top = '0';
      fireworksContainer.style.left = '0';
      fireworksContainer.style.width = '100%';
      fireworksContainer.style.height = '100%';
      fireworksContainer.style.pointerEvents = 'none';
      fireworksContainer.style.zIndex = '9999';
      fireworksContainer.style.overflow = 'hidden';
      document.body.appendChild(fireworksContainer);

      // Create periodic fireworks
      const createFirework = () => {
        // Random position for firework launch
        const startX = Math.random() * 100;
        
        // Create rocket
        const rocket = document.createElement('div');
        rocket.style.width = '4px';
        rocket.style.height = '20px';
        rocket.style.position = 'absolute';
        rocket.style.left = `${startX}%`;
        rocket.style.bottom = '0';
        rocket.style.background = 'linear-gradient(to top, #FFD700, #FF4500)';
        rocket.style.boxShadow = '0 0 8px #FFD700';
        rocket.style.borderRadius = '2px';
        rocket.style.pointerEvents = 'none';
        
        // Random target height (30% to 70% from top)
        const targetY = Math.random() * 40 + 30;
        rocket.style.setProperty('--target-y', `-${targetY}vh`);
        
        // Random rise duration (1-3 seconds)
        const riseDuration = Math.random() * 2 + 1;
        rocket.style.animation = `rocket-rise ${riseDuration}s ease-out forwards`;
        
        fireworksContainer.appendChild(rocket);
        
        // Create explosion after rocket reaches target
        setTimeout(() => {
          if (rocket.parentNode) {
            rocket.parentNode.removeChild(rocket);
          }
          
          // Create explosion at rocket's position
          createExplosion(startX, targetY);
        }, riseDuration * 1000);
      };
      
      // Create explosion effect
      const createExplosion = (x, y) => {
        // Create multiple debris particles for each explosion
        const debrisCount = Math.floor(Math.random() * 50) + 50; // 50-100 pieces for more intensity
        
        for (let i = 0; i < debrisCount; i++) {
          const debris = document.createElement('div');
          
          // Random size between 2px and 6px
          const size = Math.random() * 4 + 2;
          debris.style.width = `${size}px`;
          debris.style.height = `${size}px`;
          
          // Position at explosion center
          debris.style.position = 'absolute';
          debris.style.left = `${x}%`;
          debris.style.top = `${100 - y}%`;
          
          // Rich CNY color palette (red, gold, pink, orange, yellow)
          const colors = [
            '#FF0000', '#DC143C', '#B22222', // Reds
            '#FFD700', '#FFA500', '#FF8C00', '#DAA520', // Golds/Oranges
            '#FFC0CB', '#FF69B4', '#FF1493', // Pinks
            '#FFFF00', '#FFEA00' // Yellows
          ];
          const color = colors[Math.floor(Math.random() * colors.length)];
          debris.style.background = color;
          debris.style.boxShadow = `0 0 ${size * 2}px ${color}`;
          
          // Mostly circles with some spark shapes
          if (Math.random() > 0.3) {
            debris.style.borderRadius = '50%';
          }
          
          debris.style.opacity = Math.random() * 0.8 + 0.2;
          debris.style.pointerEvents = 'none';
          
          // Random movement direction and distance
          const angle = Math.random() * Math.PI * 2; // Random angle in radians
          const distance = Math.random() * 300 + 100; // 100-400px distance for bigger spread
          const tx = Math.cos(angle) * distance;
          const ty = Math.sin(angle) * distance;
          
          // Store target positions as CSS variables
          debris.style.setProperty('--tx', `${tx}px`);
          debris.style.setProperty('--ty', `${ty}px`);
          
          // Random explosion animation duration (0.8-2.5 seconds)
          const explodeDuration = Math.random() * 1.7 + 0.8;
          debris.style.animation = `cny-explode ${explodeDuration}s cubic-bezier(0.1, 0.8, 0.2, 1) forwards`;
          
          // Random delay for cascading effect
          const delay = Math.random() * 0.5;
          debris.style.animationDelay = `${delay}s`;
          
          fireworksContainer.appendChild(debris);
          
          // Remove debris after animation completes
          setTimeout(() => {
            if (debris.parentNode) {
              debris.parentNode.removeChild(debris);
            }
          }, explodeDuration * 1000 + delay * 1000);
        }
        
        // Occasionally create a secondary explosion for more drama
        if (Math.random() > 0.7) {
          setTimeout(() => {
            createSecondaryExplosion(x, y);
          }, 300);
        }
      };
      
      // Create secondary explosion for more complex effects
      const createSecondaryExplosion = (x, y) => {
        const secondaryDebrisCount = Math.floor(Math.random() * 30) + 20; // 20-50 pieces
        
        for (let i = 0; i < secondaryDebrisCount; i++) {
          const debris = document.createElement('div');
          
          // Smaller particles for secondary explosion
          const size = Math.random() * 3 + 1;
          debris.style.width = `${size}px`;
          debris.style.height = `${size}px`;
          
          // Position near original explosion
          const offsetX = (Math.random() - 0.5) * 20;
          const offsetY = (Math.random() - 0.5) * 20;
          debris.style.position = 'absolute';
          debris.style.left = `${x + offsetX / window.innerWidth * 100}%`;
          debris.style.top = `${100 - y + offsetY / window.innerHeight * 100}%`;
          
          // Different color palette for secondary explosion
          const colors = ['#FFFFFF', '#FFD700', '#FF4500'];
          const color = colors[Math.floor(Math.random() * colors.length)];
          debris.style.background = color;
          debris.style.boxShadow = `0 0 ${size * 3}px ${color}`;
          debris.style.borderRadius = '50%';
          
          debris.style.opacity = Math.random() * 0.7 + 0.3;
          debris.style.pointerEvents = 'none';
          
          // Movement in tighter pattern
          const angle = Math.random() * Math.PI * 2;
          const distance = Math.random() * 150 + 50;
          const tx = Math.cos(angle) * distance;
          const ty = Math.sin(angle) * distance;
          
          debris.style.setProperty('--tx', `${tx}px`);
          debris.style.setProperty('--ty', `${ty}px`);
          
          const explodeDuration = Math.random() * 1.2 + 0.5;
          debris.style.animation = `cny-explode ${explodeDuration}s cubic-bezier(0.1, 0.8, 0.2, 1) forwards`;
          
          const delay = Math.random() * 0.3;
          debris.style.animationDelay = `${delay}s`;
          
          fireworksContainer.appendChild(debris);
          
          setTimeout(() => {
            if (debris.parentNode) {
              debris.parentNode.removeChild(debris);
            }
          }, explodeDuration * 1000 + delay * 1000);
        }
      };

      // Create more frequent fireworks (every 1.5 seconds instead of 3)
      const fireworksInterval = setInterval(createFirework, 1500);
      
      // Initial firework after a short delay
      setTimeout(createFirework, 500);

      // Cleanup function
      return () => {
        clearInterval(fireworksInterval);
        if (document.body.contains(fireworksContainer)) {
          document.body.removeChild(fireworksContainer);
        }
      };
    } else if (theme === 'newyear') {
      // Initialize fireworks service for New Year theme
      initFireworks();
      
      // Cleanup function
      return () => {
        destroyFireworks();
      };
    } else if (theme === 'default' || theme === 'spring' || theme === 'summer' || theme === 'halloween') {
      // Create bubbles container for default theme
      const bubblesContainer = document.createElement('div');
      bubblesContainer.className = 'bubbles';
      bubblesContainer.style.position = 'fixed';
      bubblesContainer.style.top = '0';
      bubblesContainer.style.left = '0';
      bubblesContainer.style.width = '100%';
      bubblesContainer.style.height = '100%';
      bubblesContainer.style.pointerEvents = 'none';
      bubblesContainer.style.zIndex = '9998'; // Behind other elements
      bubblesContainer.style.overflow = 'hidden';
      document.body.appendChild(bubblesContainer);

      // Create bubbles
      const createBubble = () => {
        const bubble = document.createElement('div');
        
        // Random size between 2px and 10px
        const size = Math.random() * 8 + 2;
        bubble.style.width = `${size}px`;
        bubble.style.height = `${size}px`;
        
        // Random position
        bubble.style.position = 'absolute';
        bubble.style.left = `${Math.random() * 100}%`;
        bubble.style.bottom = `-10px`; // Start below viewport
        
        // Styling - yellowish lights rising up
        bubble.style.background = 'rgba(15, 47, 252, 0.85)';
        bubble.style.border = '1px solid rgba(0, 191, 255, 0.9)';
        bubble.style.borderRadius = '50%';
        bubble.style.boxShadow = '0 0 15px rgba(143, 214, 255, 1)';
        bubble.style.opacity = Math.random() * 0.3 + 0.7;
        bubble.style.pointerEvents = 'none';
        
        // Random rise animation (8-15 seconds)
        const riseDuration = Math.random() * 7 + 8;
        bubble.style.animation = `rise ${riseDuration}s linear infinite`;
        
        // Random animation delay
        const delay = Math.random() * 5;
        bubble.style.animationDelay = `${delay}s`;
        
        bubblesContainer.appendChild(bubble);
        
        // Remove bubble after animation completes
        setTimeout(() => {
          if (bubble.parentNode) {
            bubble.parentNode.removeChild(bubble);
          }
        }, riseDuration * 1000);
      };

      // Create initial batch of bubbles
      const initialTimeouts = [];
      for (let i = 0; i < 20; i++) {
        const timeoutId = setTimeout(() => {
          createBubble();
        }, i * 300); // Stagger initial bubbles
        initialTimeouts.push(timeoutId);
      }

      // Continue creating bubbles periodically
      const bubblesInterval = setInterval(createBubble, 800);

      // Cleanup function
      return () => {
        initialTimeouts.forEach(clearTimeout);
        clearInterval(bubblesInterval);
        if (document.body.contains(bubblesContainer)) {
          document.body.removeChild(bubblesContainer);
        }
      };
    }
  }, [theme]);

  return (
    <AuthProvider>
      <LanguageContext.Provider value={{ language, setLanguage }}>
        <ThemeContext.Provider value={{ theme, setTheme }}>
          <Router>
            <div className={`app theme-${theme} ${process.env.NODE_ENV === 'development' ? 'development' : ''}`} id="app-container">
              <Header />
              <ThemeSwitcher />
              {/* Christmas Elements */}
              {theme === 'christmas' && (
                <>
                  <div className="christmas-tree">
                    <div className="tree-layer"></div>
                    <div className="tree-top"></div>
                    <div className="tree-star"></div>
                  </div>
                  <div className="santa-claus">
                    <div className="santa-head"></div>
                    <div className="santa-hat"></div>
                    <div className="santa-body"></div>
                  </div>
                </>
              )}
              <main>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/:id" element={<ProductDetail />} />
                  <Route path="/services/:categoryKey" element={<ServiceDetail />} />
                  <Route path="/admin/login" element={<Login />} />
                  <Route path="/admin" element={
                    <ProtectedRoute>
                      <AdminPanel />
                    </ProtectedRoute>
                  } />
                  <Route path="/portfolio" element={<Portfolio />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/privacy" element={<Privacy />} />
                </Routes>
              </main>
              <Footer />
              <PerformanceReport />
            </div>
          </Router>
        </ThemeContext.Provider>
      </LanguageContext.Provider>
    </AuthProvider>
  );
}

export default App;